import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import ferroImage from '@public/images/use-ferro.png';
import tectonicImage from '@public/images/use-tectonic.png';
import vvsImage from '@public/images/use-vvs.png';
import { useIsDesktop } from '@ui/components/MobileOrDesktop';
import { SlidablePanel } from '@ui/components/SlidablePanel';
import { SwiperSlide } from '@ui/components/Swiper';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { CoinModeProvider } from '@ui/hooks/useCoinMode';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import type { GardenType } from '@ui/utils/garden';
import { getGardenName } from '@ui/utils/garden';
import { GardenWaterBoostCard } from '@ui/views/Garden/GardenBoostCard/GardenWaterBoostCard';
import { inMobileSelector } from '@veno-app/chakra';
import NextImage from 'next/image';
import React from 'react';

import { features } from '../Features';
import { GardenInfoCard } from './GardenInfoCard';
import GardenInfoSlide from './GardenInfoSlide';

const tectonicEnabled = isFeatureEnabled(features.TectonicGarden);
const atomTectonicGardenEnabled = isFeatureEnabled(features.AtomTectonicGarden);
const gardens: GardenType[] = tectonicEnabled
  ? ['VVS', 'FERRO', 'TECTONIC']
  : ['VVS', 'FERRO'];

function GardenInfoView() {
  const t = useTranslations();
  const isDesktop = useIsDesktop();

  return (
    <Flex mt="60px" flexDir="column">
      <Text
        flex="1"
        textStyle="h4Bold"
        textAlign={{
          base: 'center',
          desktop: 'left',
        }}
      >
        {t('Gardens')}
      </Text>
      <GardenWaterBoostCard
        isExpandable={false}
        boxShadow="unset"
        p="0"
        mt={{
          base: '24px',
          desktop: '32px',
        }}
      />
      {isFeatureEnabled(features.Atom) ? (
        <>
          <GardenInfoCard
            coinMode="cro"
            gardenType="VVS"
            colorTheme="default"
            mt="19px"
          />
          <Tabs
            mt="32px"
            sx={{
              [inMobileSelector]: {
                mx: '-16px',
              },
            }}
          >
            <TabList>
              <Tab>CRO</Tab>
              <Tab>ATOM</Tab>
            </TabList>
            <TabPanels>
              {(['cro', 'atom'] as CoinMode[]).map((coinMode) => {
                return (
                  <CoinModeProvider key={coinMode} value={coinMode}>
                    <TabPanel py="32px">
                      {coinMode === 'cro' && (
                        <GardenInfoCard
                          coinMode={coinMode}
                          gardenType="VVS_CRO"
                          mb="16px"
                        />
                      )}
                      <GardenInfoCard coinMode={coinMode} gardenType="FERRO" />
                      {(coinMode !== 'atom' || atomTectonicGardenEnabled) && (
                        <GardenInfoCard
                          coinMode={coinMode}
                          gardenType="TECTONIC"
                          mt="16px"
                        />
                      )}
                    </TabPanel>
                  </CoinModeProvider>
                );
              })}
            </TabPanels>
          </Tabs>
        </>
      ) : (
        <Box mt="52px" mb={3}>
          <SlidablePanel
            hideSlideButton={!isDesktop}
            allowTouchMove={true}
            hideSlideIndicator={false}
            title={SliderTitle}
          >
            <SwiperSlide>
              <GardenInfoSlide gardenType="VVS" />
            </SwiperSlide>
            <SwiperSlide>
              <GardenInfoSlide gardenType="FERRO" />
            </SwiperSlide>
            {tectonicEnabled && (
              <SwiperSlide>
                <GardenInfoSlide gardenType="TECTONIC" />
              </SwiperSlide>
            )}
          </SlidablePanel>
        </Box>
      )}
    </Flex>
  );
}

/**
 * this code will be only used in the cro mode
 * @deprecated todo cover.lv remove this code after the ATOM release
 */
const SliderTitle = ({ swiperIndex }: { swiperIndex: number }) => {
  return (
    <Flex
      justify={{
        base: 'center',
        desktop: 'flex-start',
      }}
      gap={1}
    >
      <Flex w="24px" h="24px" justify="center" align="center" rounded="full">
        <NextImage
          alt="Brand Image"
          src={getGardeImage(gardens[swiperIndex])}
        />
      </Flex>
      <Box textStyle="h4Bold">{getGardenName('cro', gardens[swiperIndex])}</Box>
    </Flex>
  );
};

const getGardeImage = (garden: GardenType) => {
  switch (garden) {
    case 'TECTONIC':
      return tectonicImage;
    case 'FERRO':
      return ferroImage;
    case 'VVS':
    case 'VVS_CRO':
      return vvsImage;
  }
};

export default GardenInfoView;
